import { useEffect } from 'react'
import { useTranslation } from '../hooks/useTranslation'
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function Privacy() {
  const { t, getPageTitle, lang } = useTranslation()

  useEffect(() => {
    document.title = getPageTitle('privacy')
  }, [lang, getPageTitle])

  return (
    <div className="privacy-page">
      <Header />

      {/* Hero Section */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content">
          <h1>{texts[lang]?.title || texts.en.title}</h1>
          <p>{texts[lang]?.subtitle || texts.en.subtitle}</p>
          <p style={{ fontSize: '14px', color: 'var(--text)', marginTop: '16px' }}>
            {texts[lang]?.lastUpdated || texts.en.lastUpdated}: 2026-04-12
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="container">
          <div className="privacy-content">
            <section className="privacy-section">
              <h2>1. {texts[lang]?.infoCollectTitle || texts.en.infoCollectTitle}</h2>
              <h3>1.1 {texts[lang]?.infoProvideTitle || texts.en.infoProvideTitle}</h3>
              <ul>
                {(texts[lang]?.infoProvideList || texts.en.infoProvideList).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <h3>1.2 {texts[lang]?.infoAutoTitle || texts.en.infoAutoTitle}</h3>
              <ul>
                {(texts[lang]?.infoAutoList || texts.en.infoAutoList).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="privacy-section">
              <h2>2. {texts[lang]?.useInfoTitle || texts.en.useInfoTitle}</h2>
              <ul>
                {(texts[lang]?.useInfoList || texts.en.useInfoList).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="privacy-section">
              <h2>3. {texts[lang]?.cookiesTitle || texts.en.cookiesTitle}</h2>
              <p>{texts[lang]?.cookiesDesc || texts.en.cookiesDesc}</p>
            </section>

            <section className="privacy-section">
              <h2>4. {texts[lang]?.dataSharingTitle || texts.en.dataSharingTitle}</h2>
              <p>{texts[lang]?.dataSharingDesc || texts.en.dataSharingDesc}</p>
            </section>

            <section className="privacy-section">
              <h2>5. {texts[lang]?.yourRightsTitle || texts.en.yourRightsTitle}</h2>
              <p style={{ marginBottom: '16px' }}>{texts[lang]?.yourRightsDesc || texts.en.yourRightsDesc}</p>
              <ul>
                {(texts[lang]?.rightsList || texts.en.rightsList).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="privacy-section">
              <h2>6. {texts[lang]?.gdprTitle || texts.en.gdprTitle}</h2>
              <p>{texts[lang]?.gdprDesc || texts.en.gdprDesc}</p>
            </section>

            <section className="privacy-section">
              <h2>7. {texts[lang]?.contactTitle || texts.en.contactTitle}</h2>
              <p>{texts[lang]?.contactDesc || texts.en.contactDesc}</p>
              <p style={{ marginTop: '12px' }}>
                <strong>Email:</strong>{' '}
                <a href="mailto:Prime@ccicm.co" style={{ color: 'var(--gold)' }}>Prime@ccicm.co</a>
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

const texts = {
  en: {
    title: 'Privacy Policy',
    subtitle: 'Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.',
    lastUpdated: 'Last Updated',
    infoCollectTitle: 'Information We Collect',
    infoProvideTitle: 'Information You Provide',
    infoProvideList: ['Name', 'Email address', 'Phone number', 'Company name', 'Messages and inquiries'],
    infoAutoTitle: 'Automatically Collected Information',
    infoAutoList: ['IP address', 'Browser type', 'Device information', 'Pages visited on our website'],
    useInfoTitle: 'How We Use Your Information',
    useInfoList: [
      'To respond to your inquiries and provide customer support',
      'To improve our website and services',
      'To send service-related communications',
      'To comply with legal obligations and protect our rights'
    ],
    cookiesTitle: 'Cookies',
    cookiesDesc: 'Our website uses cookies to enhance your browsing experience and analyze site traffic. You can control cookie preferences through our Cookie Consent Banner at any time.',
    dataSharingTitle: 'Data Sharing and Disclosure',
    dataSharingDesc: 'We do not sell, trade, or rent your personal information to third parties. We may share your information only with service providers who assist us in operating our website and conducting our business, subject to confidentiality obligations.',
    yourRightsTitle: 'Your Rights',
    yourRightsDesc: 'You have the following rights regarding your personal data:',
    rightsList: [
      'The right to access your personal data',
      'The right to correction of inaccurate or incomplete data',
      'The right to deletion of your data ("right to be forgotten")',
      'The right to restriction of processing',
      'The right to data portability',
      'The right to object to processing'
    ],
    gdprTitle: 'GDPR Compliance',
    gdprDesc: 'For users in the European Economic Area (EEA), we process personal data in accordance with the General Data Protection Regulation (GDPR). Our legal basis for processing depends on the context and the data involved.',
    contactTitle: 'Contact Us',
    contactDesc: 'If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us using the information below.'
  },
  zh: {
    title: '隐私政策',
    subtitle: '您的隐私对我们很重要。本政策解释我们如何收集、使用和保护您的个人信息。',
    lastUpdated: '最后更新',
    infoCollectTitle: '我们收集的信息',
    infoProvideTitle: '您提供的信息',
    infoProvideList: ['姓名', '电子邮箱地址', '电话号码', '公司名称', '留言和咨询'],
    infoAutoTitle: '自动收集的信息',
    infoAutoList: ['IP 地址', '浏览器类型', '设备信息', '您访问的页面'],
    useInfoTitle: '我们如何使用您的信息',
    useInfoList: [
      '回复您的咨询并提供客户支持',
      '改进我们的网站和服务',
      '发送服务相关通知',
      '履行法律义务并保护我们的权利'
    ],
    cookiesTitle: 'Cookie',
    cookiesDesc: '我们的网站使用 Cookie 来提升您的浏览体验并分析网站流量。您可以随时通过我们的 Cookie 同意横幅来控制 Cookie 偏好。',
    dataSharingTitle: '数据共享和披露',
    dataSharingDesc: '我们不会出售、交易或出租您的个人信息给第三方。我们可能仅与协助我们运营网站和开展业务的 service 提供商共享您的信息，且他们需遵守保密义务。',
    yourRightsTitle: '您的权利',
    yourRightsDesc: '关于您的个人数据，您拥有以下权利：',
    rightsList: [
      '访问您的个人数据的权利',
      '更正不准确或不完整数据的权利',
      '删除您的数据的权利（"被遗忘权"）',
      '限制处理的权利',
      '数据可携带权',
      '反对处理的权利'
    ],
    gdprTitle: 'GDPR 合规',
    gdprDesc: '对于欧洲经济区 (EEA) 的用户，我们根据《通用数据保护条例》(GDPR) 处理个人数据。我们处理数据的法律依据取决于具体情况和所涉及的数据。',
    contactTitle: '联系我们',
    contactDesc: '如果您对本隐私政策有任何疑问或希望行使您的数据保护权利，请使用以下信息联系我们。'
  },
  fr: {
    title: 'Politique de confidentialité',
    subtitle: 'Votre vie privée est importante pour nous. Cette politique explique comment nous collectons, utilisons et protégeons vos informations personnelles.',
    lastUpdated: 'Dernière mise à jour',
    infoCollectTitle: 'Informations que nous collectons',
    infoProvideTitle: 'Informations que vous fournissez',
    infoProvideList: ['Nom', 'Adresse e-mail', 'Numéro de téléphone', 'Nom de l\'entreprise', 'Messages et demandes'],
    infoAutoTitle: 'Informations collectées automatiquement',
    infoAutoList: ['Adresse IP', 'Type de navigateur', 'Informations sur l\'appareil', 'Pages visitées'],
    useInfoTitle: 'Comment nous utilisons vos informations',
    useInfoList: [
      'Répondre à vos demandes et fournir un support client',
      'Améliorer notre site web et nos services',
      'Envoyer des communications relatives au service',
      'Respecter les obligations légales et protéger nos droits'
    ],
    cookiesTitle: 'Cookies',
    cookiesDesc: 'Notre site utilise des cookies pour améliorer votre expérience de navigation et analyser le trafic du site. Vous pouvez contrôler les préférences de cookies via notre bannière de consentement.',
    dataSharingTitle: 'Partage et divulgation des données',
    dataSharingDesc: 'Nous ne vendons, n\'échangeons ni ne louons vos informations personnelles à des tiers. Nous pouvons partager vos informations uniquement avec des fournisseurs de services qui nous aident.',
    yourRightsTitle: 'Vos droits',
    yourRightsDesc: 'Vous disposez des droits suivants concernant vos données personnelles:',
    rightsList: [
      'Droit d\'accès à vos données personnelles',
      'Droit de rectification des données inexactes',
      'Droit à l\'effacement des données ("droit à l\'oubli")',
      'Droit à la limitation du traitement',
      'Droit à la portabilité des données',
      'Droit d\'opposition au traitement'
    ],
    gdprTitle: 'Conformité RGPD',
    gdprDesc: 'Pour les utilisateurs de l\'Espace économique européen (EEE), nous traitons les données personnelles conformément au Règlement général sur la protection des données (RGPD).',
    contactTitle: 'Nous contacter',
    contactDesc: 'Si vous avez des questions concernant cette politique de confidentialité, veuillez nous contacter.'
  },
  es: {
    title: 'Política de privacidad',
    subtitle: 'Su privacidad es importante para nosotros. Esta política explica cómo recopilamos, usamos y protegemos su información personal.',
    lastUpdated: 'Última actualización',
    infoCollectTitle: 'Información que recopilamos',
    infoProvideTitle: 'Información que usted proporciona',
    infoProvideList: ['Nombre', 'Correo electrónico', 'Número de teléfono', 'Nombre de la empresa', 'Mensajes y consultas'],
    infoAutoTitle: 'Información recopilada automáticamente',
    infoAutoList: ['Dirección IP', 'Tipo de navegador', 'Información del dispositivo', 'Páginas visitadas'],
    useInfoTitle: 'Cómo usamos su información',
    useInfoList: [
      'Responder a sus consultas y proporcionar atención al cliente',
      'Mejorar nuestro sitio web y servicios',
      'Enviar comunicaciones relacionadas con el servicio',
      'Cumplir con obligaciones legales y proteger nuestros derechos'
    ],
    cookiesTitle: 'Cookies',
    cookiesDesc: 'Nuestro sitio web utiliza cookies para mejorar su experiencia de navegación y analizar el tráfico del sitio. Puede controlar las preferencias de cookies a través de nuestra banner de consentimiento.',
    dataSharingTitle: 'Compartir y divulgar datos',
    dataSharingDesc: 'No vendemos, intercambiamos ni alquilamos su información personal a terceros. Podemos compartir su información solo con proveedores de servicios que nos ayudan.',
    yourRightsTitle: 'Sus derechos',
    yourRightsDesc: 'Usted tiene los siguientes derechos con respecto a sus datos personales:',
    rightsList: [
      'Derecho de acceso a sus datos personales',
      'Derecho de rectificación de datos inexactos',
      'Derecho de supresión de datos ("derecho al olvido")',
      'Derecho a la limitación del tratamiento',
      'Derecho a la portabilidad de datos',
      'Derecho de oposición al tratamiento'
    ],
    gdprTitle: 'Cumplimiento del RGPD',
    gdprDesc: 'Para usuarios del Espacio Económico Europeo (EEE), procesamos datos personales de acuerdo con el Reglamento General de Protección de Datos (RGPD).',
    contactTitle: 'Contáctenos',
    contactDesc: 'Si tiene preguntas sobre esta política de privacidad, contáctenos.'
  },
  pt: {
    title: 'Política de Privacidade',
    subtitle: 'Sua privacidade é importante para nós. Esta política explica como coletamos, usamos e protegemos suas informações pessoais.',
    lastUpdated: 'Última atualização',
    infoCollectTitle: 'Informações que coletamos',
    infoProvideTitle: 'Informações que você fornece',
    infoProvideList: ['Nome', 'E-mail', 'Número de telefone', 'Nome da empresa', 'Mensagens e consultas'],
    infoAutoTitle: 'Informações coletadas automaticamente',
    infoAutoList: ['Endereço IP', 'Tipo de navegador', 'Informações do dispositivo', 'Páginas visitadas'],
    useInfoTitle: 'Como usamos suas informações',
    useInfoList: [
      'Responder às suas consultas e fornecer suporte ao cliente',
      'Melhorar nosso site e serviços',
      'Enviar comunicações relacionadas ao serviço',
      'Cumprir obrigações legais e proteger nossos direitos'
    ],
    cookiesTitle: 'Cookies',
    cookiesDesc: 'Nosso site usa cookies para melhorar sua experiência de navegação e analisar o tráfego do site. Você pode controlar as preferências de cookies através do nosso banner de consentimento.',
    dataSharingTitle: 'Compartilhamento e divulgação de dados',
    dataSharingDesc: 'Não vendemos, trocamos ou alugamos suas informações pessoais a terceiros. Podemos compartilhar suas informações apenas com provedores de serviços que nos ajudam.',
    yourRightsTitle: 'Seus direitos',
    yourRightsDesc: 'Você tem os seguintes direitos em relação aos seus dados pessoais:',
    rightsList: [
      'Direito de acesso aos seus dados pessoais',
      'Direito de retificação de dados imprecisos',
      'Direito de exclusão de dados ("direito ao esquecimento")',
      'Direito à limitação do tratamento',
      'Direito à portabilidade de dados',
      'Direito de oposição ao tratamento'
    ],
    gdprTitle: 'Conformidade com o RGPD',
    gdprDesc: 'Para usuários do Espaço Econômico Europeu (EEE), processamos dados pessoais de acordo com o Regulamento Geral de Proteção de Dados (RGPD).',
    contactTitle: 'Contate-nos',
    contactDesc: 'Se você tiver dúvidas sobre esta política de privacidade, entre em contato conosco.'
  },
  'zh-HK': {
    title: '隱私政策',
    subtitle: '您的私隱對我們很重要。本政策解釋我們如何收集、使用和保護您的個人資料。',
    lastUpdated: '最後更新',
    infoCollectTitle: '我們收集的資料',
    infoProvideTitle: '您提供的資料',
    infoProvideList: ['姓名', '電子郵件地址', '電話號碼', '公司名稱', '留言和查詢'],
    infoAutoTitle: '自動收集的資料',
    infoAutoList: ['IP 地址', '瀏覽器類型', '裝置資訊', '您瀏覽的頁面'],
    useInfoTitle: '我們如何使用您的資料',
    useInfoList: [
      '回覆您的查詢並提供客戶支援',
      '改進我們的網站和服務',
      '發送服務相關通知',
      '履行法律義務並保護我們的權利'
    ],
    cookiesTitle: 'Cookie',
    cookiesDesc: '我們的網站使用 Cookie 來提升您的瀏覽體驗並分析網站流量。您可以隨時透過我們的 Cookie 同意橫幅來控制 Cookie 偏好。',
    dataSharingTitle: '資料共享和披露',
    dataSharingDesc: '我們不會出售、交易或出租您的個人資料給第三方。我們可能僅與協助我們營運網站和開展業務的服務提供者共享您的資料，且他們需遵守保密義務。',
    yourRightsTitle: '您的權利',
    yourRightsDesc: '關於您的個人資料，您擁有以下權利：',
    rightsList: [
      '存取您的個人資料的權利',
      '更正不準確或不完整資料的權利',
      '刪除您的資料的權利（「被遺忘權」）',
      '限制處理的權利',
      '資料可攜權',
      '反對處理的權利'
    ],
    gdprTitle: 'GDPR 合規',
    gdprDesc: '對於歐洲經濟區 (EEA) 的使用者，我們根據《通用資料保護規範》(GDPR) 處理個人資料。我們處理資料的法律依據取決於具體情況和所涉及的資料。',
    contactTitle: '聯絡我們',
    contactDesc: '如果您對本隱私政策有任何疑問或希望行使您的資料保護權利，請使用以下資訊聯絡我們。'
  },
  ko: {
    title: '개인정보처리방침',
    subtitle: '당신의隐私는 우리에게 중요합니다. 이 정책은 우리가 귀하의 개인정보를 어떻게 수집, 사용 및 보호하는지 설명합니다.',
    lastUpdated: '최종 업데이트',
    infoCollectTitle: '수집하는 정보',
    infoProvideTitle: '귀하가 제공하는 정보',
    infoProvideList: ['이름', '이메일 주소', '전화번호', '회사명', '메시지 및 문의'],
    infoAutoTitle: '자동으로 수집되는 정보',
    infoAutoList: ['IP 주소', '브라우저 유형', '장치 정보', '방문한 페이지'],
    useInfoTitle: '정보 사용 방법',
    useInfoList: [
      '문의에 응답하고 고객 지원 제공',
      '웹사이트 및 서비스 개선',
      '서비스 관련 통신 발송',
      '법적 의무 준수 및 권리 보호'
    ],
    cookiesTitle: '쿠키',
    cookiesDesc: '당사 웹사이트는 브라우저징 경험을 개선하고 사이트 트래픽을 분석하기 위해 쿠키를 사용합니다. 쿠키 동의 배너를 통해 언제든지 쿠키 선호도를 제어할 수 있습니다.',
    dataSharingTitle: '데이터 공유 및 공개',
    dataSharingDesc: '당사는 귀하의 개인정보를 제 3 자에게 판매, 거래 또는 임대하지 않습니다. 당사는 비즈니스 운영을 지원하는 서비스 제공업체와만 정보를 공유할 수 있습니다.',
    yourRightsTitle: '귀하의 권리',
    yourRightsDesc: '귀하는 개인정보에 대해 다음과 같은 권리를 가집니다:',
    rightsList: [
      '개인정보에 대한 접근 권리',
      '부정확한 데이터 정정 권리',
      '데이터 삭제 권리 (잊혀질 권리)',
      '처리 제한 권리',
      '데이터 이동성 권리',
      '처리에 대한 이의 제기 권리'
    ],
    gdprTitle: 'GDPR 준수',
    gdprDesc: '유럽 경제 지역 (EEA) 사용자의 경우, 당사는 일반 데이터 보호 규정 (GDPR) 에 따라 개인 데이터를 처리합니다.',
    contactTitle: '문의하기',
    contactDesc: '이 개인정보처리방침에 대해 질문이 있거나 데이터 보호 권리를 행사하려는 경우 당사는 연락주시기 바랍니다.'
  },
  ja: {
    title: 'プライバシーポリシー',
    subtitle: 'お客様のプライバシーは私たちにとって重要です。このポリシーは、私たちがどのように個人情報を収集、使用、保護するかを説明します。',
    lastUpdated: '最終更新日',
    infoCollectTitle: '収集する情報',
    infoProvideTitle: 'お客様が提供する情報',
    infoProvideList: ['お名前', 'メールアドレス', '電話番号', '会社名', 'メッセージと問い合わせ'],
    infoAutoTitle: '自動的に収集される情報',
    infoAutoList: ['IP アドレス', 'ブラウザの種類', 'デバイス情報', '訪問したページ'],
    useInfoTitle: '情報の使用方法',
    useInfoList: [
      '問い合わせへの対応とカスタマーサポートの提供',
      'ウェブサイトとサービスの改善',
      'サービス関連の通信の送信',
      '法的義務の遵守と権利の保護'
    ],
    cookiesTitle: 'Cookie',
    cookiesDesc: '当ウェブサイトは、ブラウジング体験の向上とサイトトラフィックの分析のために Cookie を使用します。Cookie 同意バナーを通じて、いつでも Cookie 設定を制御できます。',
    dataSharingTitle: 'データの共有と開示',
    dataSharingDesc: '当社は、お客様の個人情報を第三者に販売、交換、貸与することはありません。ビジネス運営を支援するサービスプロバイダーとのみ情報を共有する場合があります。',
    yourRightsTitle: 'お客様の権利',
    yourRightsDesc: 'お客様は個人情報に関して以下の権利を有します:',
    rightsList: [
      '個人情報へのアクセス権',
      '不正確なデータの修正権',
      'データの削除権（忘れられる権利）',
      '処理の制限権',
      'データポータビリティ権',
      '処理への異議申立権'
    ],
    gdprTitle: 'GDPR 準拠',
    gdprDesc: '欧州経済領域 (EEA) のユーザーの場合、当社は一般データ保護規則 (GDPR) に準拠して個人データを処理します。',
    contactTitle: 'お問い合わせ',
    contactDesc: 'このプライバシーポリシーについてご質問がある場合、またはデータ保護権利を行使したい場合は、下記までご連絡ください。'
  },
  vi: {
    title: 'Chính sách quyền rieng tư',
    subtitle: 'Quyền riêng tư của bạn rất quan trọng đối với chúng tôi. Chính sách này giải thích cách chúng tôi thu thập, sử dụng và bảo vệ thông tin cá nhân của bạn.',
    lastUpdated: 'Cập nhật lần cuối',
    infoCollectTitle: 'Thông tin chúng tôi thu thập',
    infoProvideTitle: 'Thông tin bạn cung cấp',
    infoProvideList: ['Tên', 'Địa chỉ email', 'Số điện thoại', 'Tên công ty', 'Tin nhắn và yêu cầu'],
    infoAutoTitle: 'Thông tin tự động thu thập',
    infoAutoList: ['Địa chỉ IP', 'Loại trình duyệt', 'Thông tin thiết bị', 'Các trang đã truy cập'],
    useInfoTitle: 'Cách chúng tôi sử dụng thông tin của bạn',
    useInfoList: [
      'Phản hồi các yêu cầu của bạn và cung cấp hỗ trợ khách hàng',
      'Cải thiện trang web và dịch vụ của chúng tôi',
      'Gửi thông báo liên quan đến dịch vụ',
      'Tuân thủ nghĩa vụ pháp lý và bảo vệ quyền lợi của chúng tôi'
    ],
    cookiesTitle: 'Cookie',
    cookiesDesc: 'Trang web của chúng tôi sử dụng cookie để nâng cao trải nghiệm duyệt web của bạn và phân tích lưu lượng truy cập. Bạn có thể kiểm soát tùy chọn cookie thông qua Biểu mẫu đồng ý Cookie.',
    dataSharingTitle: 'Chia sẻ và tiết lộ dữ liệu',
    dataSharingDesc: 'Chúng tôi không bán, trao đổi hoặc cho thuê thông tin cá nhân của bạn cho bên thứ ba. Chúng tôi chỉ có thể chia sẻ thông tin của bạn với các nhà cung cấp dịch vụ hỗ trợ chúng tôi.',
    yourRightsTitle: 'Quyền của bạn',
    yourRightsDesc: 'Bạn có các quyền sau đây liên quan đến dữ liệu cá nhân của mình:',
    rightsList: [
      'Quyền truy cập dữ liệu cá nhân của bạn',
      'Quyền sửa chữa dữ liệu không chính xác',
      'Quyền xóa dữ liệu ("quyền bị lãng quên")',
      'Quyền hạn chế xử lý',
      'Quyền di động dữ liệu',
      'Quyền phản đối xử lý'
    ],
    gdprTitle: 'Tuân thủ GDPR',
    gdprDesc: 'Đối với người dùng trong Khu vực Kinh tế Châu Âu (EEA), chúng tôi xử lý dữ liệu cá nhân theo Quy định Bảo vệ Dữ liệu Chung (GDPR).',
    contactTitle: 'Liên hệ với chúng tôi',
    contactDesc: 'Nếu bạn có bất kỳ câu hỏi nào về chính sách quyền riêng tư này, vui lòng liên hệ với chúng tôi.'
  },
  mn: {
    title: 'Нууцлалын бодлого',
    subtitle: 'Таны хувийн мэдээлэл бидний хувьд чухал. Энэхүү бодлого нь бид таны хувийн мэдээллийг хэрхэн цуглуулах, ашиглах, хамгаалахыг тайлбарладаг.',
    lastUpdated: 'Сүүлийн шинэчлэл',
    infoCollectTitle: 'Бидний цуглуулдаг мэдээлэл',
    infoProvideTitle: 'Таны өгдөг мэдээлэл',
    infoProvideList: ['Нэр', 'Имэйл хаяг', 'Утасны дугаар', 'Компанийн нэр', 'Мессеж болон асуулга'],
    infoAutoTitle: 'Автоматаар цуглуулдаг мэдээлэл',
    infoAutoList: ['IP хаяг', 'Браузерын төрөл', 'Төхөөрөмжийн мэдээлэл', 'Үзсэн хуудсууд'],
    useInfoTitle: 'Бид таны мэдээллийг хэрхэн ашигладаг',
    useInfoList: [
      'Таны асуулгад хариулах болон үйлчлүүлэгчийн дэмжлэг үзүүлэх',
      'Манай вэбсайт болон үйлчилгээг сайжруулах',
      'Үйлчилгээтэй холбоотой мэдээлэл илгээх',
      'Хууль эрх зүйн үүргээ биелүүлэх болон эрхээ хамгаалах'
    ],
    cookiesTitle: 'Cookie',
    cookiesDesc: 'Манай вэбсайт нь таны вэб үзэх туршлагыг сайжруулах болон сайтын траффикыг шинжлэхийн тулд cookie ашигладаг. Та Cookie Зөвшөөрлийн Баннераар дамжуулан cookie тохиргоог хянах боломжтой.',
    dataSharingTitle: 'Өгөгдөл хуваалцах болон задлах',
    dataSharingDesc: 'Бид таны хувийн мэдээллийн гуравдагч этгээдэд худалдах, солилцох эсвэл түрээслэхгүй. Бид зөвхөн бидэнд тусалдаг үйлчилгээ үзүүлэгчидтэй мэдээлэл хуваалцах боломжтой.',
    yourRightsTitle: 'Таны эрх',
    yourRightsDesc: 'Таны хувийн өгөгдөлтэй холбоотой дараах эрхүүд байна:',
    rightsList: [
      'Таны хувийн өгөгдөлд хандах эрх',
      'Буруу өгөгдлийг засах эрх',
      'Өгөгдлийг устгах эрх ("мартуулах эрх")',
      'Боловсруулалтыг хязгаарлах эрх',
      'Өгөгдөл зөөвөрлөх эрх',
      'Боловсруулалтад эсэргүүцэх эрх'
    ],
    gdprTitle: 'GDPR-ийн дагаж мөрдөх',
    gdprDesc: 'Европын Эдийн Засгийн Бүс (EEA) дахь хэрэглэгчдийн хувьд бид GDPR-ийн дагуу хувийн өгөгдлийг боловсруулдаг.',
    contactTitle: 'Бидэнтэй холбогдох',
    contactDesc: 'Хэрэв танд энэхүү нууцлалын бодлогын талаар асуулт байвал бидэнтэй холбогдоно уу.'
  }
}
